import { defineMessages, type IntlShape } from 'react-intl';

/** Clinical artifact kinds Work routines may cite — mirrors BE ALLOWED_SOURCE_KINDS. */
export type SourceGroundedSourceKind =
  | 'consult_note'
  | 'transcript'
  | 'session_context'
  | 'prechart'
  | 'patient_summary'
  | 'patient_profile'
  | 'document'
  | 'library_document'
  | 'attachment'
  | 'medications'
  | 'allergies'
  | 'problems'
  | 'lab_result'
  | 'vital_signs'
  | 'imaging'
  | 'open_task'
  | 'ehr_record'
  | 'calendar_event'
  | 'agent_memory'
  | 'routine'
  | 'template'
  | 'email'
  | 'fax'
  | 'session'
  | 'unknown';

/** Structured display for free-text routine source labels from the model. */
export interface ParsedSourceGroundedLabel {
  kind: SourceGroundedSourceKind;
  /** Primary line — source kind or patient/session name. */
  title: string;
  /** Secondary line — date or other context. */
  subtitle?: string;
  /** When the label carries a session id the UI can deep-link later. */
  sessionId?: string;
  /** Library or session document id when present in the Sources suffix. */
  documentId?: string;
  /** Patient profile id when present in the Sources suffix. */
  patientId?: string;
  /** Agent memory id when present in the Sources suffix. */
  memoryId?: string;
  /** Routine / workflow id when present in the Sources suffix. */
  workflowId?: string;
  /** Template id when present in the Sources suffix. */
  templateId?: string;
  /** Normalized label for aria/title attributes. */
  displayLabel: string;
}

const DATE_LIKE_RE =
  /^(?:\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s+\d{4})?|\d{4}-\d{2}-\d{2}|\d{1,2}\/\d{1,2}(?:\/\d{2,4})?)$/i;

const SESSION_ID_RE = /\bsession_id\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

/** Legacy model output: "(session 134810265604082475505720907148572571724)". */
const SESSION_ID_PAREN_RE = /\(\s*session\s+([a-zA-Z0-9_-]+)\s*\)/i;

const DOCUMENT_ID_RE = /\bdocument_id\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const PATIENT_ID_RE = /\bpatient(?:_profile_id|_id)\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const MEMORY_ID_RE = /\bmemory_id\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const WORKFLOW_ID_RE = /\bworkflow_id\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const TEMPLATE_ID_RE = /\btemplate_id\s*[:=]\s*([a-zA-Z0-9_-]+)/i;

const METADATA_SEGMENT_RE =
  /^(?:session_id|document_id|patient(?:_profile_id|_id)|memory_id|workflow_id|template_id)\s*[:=]\s*[a-zA-Z0-9_-]+$/i;

const MARKDOWN_OR_HTML_RE = /[\[\]<>]|!\[|]\(/;
const URL_RE = /https?:\/\//i;

const KIND_RULES: { kind: SourceGroundedSourceKind; test: RegExp }[] = [
  { kind: 'consult_note', test: /consult\s*note/i },
  { kind: 'transcript', test: /transcript/i },
  { kind: 'session_context', test: /session\s*context|context\s*(?:tab|note)/i },
  { kind: 'agent_memory', test: /agent\s*memory|^memory\b/i },
  { kind: 'routine', test: /^routine\b|saved\s*skill/i },
  { kind: 'template', test: /^template\b|document\s*template/i },
  { kind: 'prechart', test: /pre[\s-]?chart/i },
  { kind: 'library_document', test: /library\s*document|my\s*library|user\s*document/i },
  { kind: 'attachment', test: /attachment|attached\s*file/i },
  { kind: 'patient_profile', test: /patient\s*profile|demographics|patient\s*info/i },
  { kind: 'patient_summary', test: /patient\s*summary|chart\s*summary/i },
  { kind: 'medications', test: /medication|medicine|med\s*rec/i },
  { kind: 'allergies', test: /allerg/i },
  { kind: 'problems', test: /problem|diagnos|active\s*issue/i },
  { kind: 'lab_result', test: /lab\s*result|pathology|blood\s*test/i },
  { kind: 'vital_signs', test: /vital\s*sign|observation/i },
  { kind: 'imaging', test: /imaging|radiology|x[\s-]?ray|ct\b|mri\b|ultrasound/i },
  { kind: 'open_task', test: /open\s*task|clinical\s*tasks?|^task\b/i },
  { kind: 'ehr_record', test: /ehr\s*record|chart\s*record/i },
  { kind: 'calendar_event', test: /calendar\s*event|appointment/i },
  { kind: 'email', test: /^email\b/i },
  { kind: 'fax', test: /^fax\b/i },
  { kind: 'document', test: /document|letter|report|pdf|upload/i },
  { kind: 'session', test: /^session\b/i },
];

export const SOURCE_LABEL_MIN_LEN = 3;
export const SOURCE_LABEL_MAX_LEN = 120;

const isDateLike = (value: string): boolean => DATE_LIKE_RE.test(value.trim());

const detectKind = (text: string): SourceGroundedSourceKind =>
  KIND_RULES.find((rule) => rule.test.test(text))?.kind ?? 'unknown';

const UNLINKED_SESSION_ID_RE = /\bunlinked\s+session\s+([a-zA-Z0-9_-]{15,})\b/i;

const extractSessionId = (text: string): string | undefined => {
  const explicit = SESSION_ID_RE.exec(text);
  if (explicit?.[1]) return explicit[1].trim();
  const paren = SESSION_ID_PAREN_RE.exec(text);
  if (paren?.[1]) return paren[1].trim();
  const unlinked = UNLINKED_SESSION_ID_RE.exec(text);
  return unlinked?.[1]?.trim() || undefined;
};

const stripSessionParenSuffix = (text: string): string =>
  text.replace(/\s*\(\s*session\s+[a-zA-Z0-9_-]+\s*\)\s*$/i, '').trim();

/** Patient-linkage phrasing the model emits — not clinician-facing label text. */
const stripLinkageLabelNoise = (text: string): string =>
  text
    .replace(/,\s*unlinked\s+session\s*\(\s*no\s+patient\s*\)/gi, '')
    .replace(/,\s*unlinked\s+session\s+"([^"]+)"/gi, ', $1')
    .replace(/,\s*unlinked\s+session\s+'([^']+)'/gi, ', $1')
    .replace(/,\s*unlinked\s+session\s+[a-zA-Z0-9_-]{15,}/gi, '')
    .replace(/,\s*unlinked\s+session\b/gi, '')
    .replace(/\(\s*no\s+patient\s*\)/gi, '')
    .replace(/\bunlinked\s+session\s+[a-zA-Z0-9_-]{15,}\b/gi, '')
    .replace(/\bunlinked\s+session\b/gi, '')
    .replace(
      /,\s*created\s+(?=\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))/gi,
      ', '
    )
    .replace(
      /\bcreated\s+(?=\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec))/gi,
      ''
    )
    .replace(/\s*[—–-]\s*no patient linked\.?\s*$/i, '')
    .replace(/\s*[—–-]\s*same clinical presentation.*$/i, '')
    .replace(/,\s*,/g, ',')
    .replace(/^,\s*|,\s*$/g, '')
    .replace(/\s{2,}/g, ' ')
    .trim();

/** Strip model-authored version/linkage noise before label parsing. */
const sanitizeModelLabel = (raw: string): string => {
  let text = stripLinkageLabelNoise(raw.trim());
  text = text.replace(/\(\s*edited\s+version\s*\)/gi, '');
  text = text.replace(/\(\s*original\s+version\s*\)/gi, '');
  text = text.replace(/\s*[—–-]\s*patient linked to profile\s+.+$/i, '');
  text = text.replace(/\s*[—–-]\s*no patient profile linked\s*$/i, '');
  text = text.replace(
    /\s+[—–-]\s+(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s+\d{4})?)\s*$/i,
    ', $1'
  );
  text = text.replace(
    /Consult note\s*,?\s*session\s+['"](.+?)['"]\s*,\s*(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s+\d{4})?)\s*$/i,
    'Session: $1 ($2)'
  );
  text = text.replace(
    /^Consult note\s*,\s*Session\s+[A-Z]\s*,\s*(\d{1,2}\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-z]*(?:\s+\d{4})?)\s*$/i,
    'Consult note, $1'
  );
  return text
    .replace(/\s{2,}/g, ' ')
    .replace(/,\s*,/g, ',')
    .trim();
};

const extractDocumentId = (text: string): string | undefined => {
  const match = DOCUMENT_ID_RE.exec(text);
  return match?.[1]?.trim() || undefined;
};

const extractPatientId = (text: string): string | undefined => {
  const match = PATIENT_ID_RE.exec(text);
  return match?.[1]?.trim() || undefined;
};

const extractMemoryId = (text: string): string | undefined => {
  const match = MEMORY_ID_RE.exec(text);
  return match?.[1]?.trim() || undefined;
};

const extractWorkflowId = (text: string): string | undefined => {
  const match = WORKFLOW_ID_RE.exec(text);
  return match?.[1]?.trim() || undefined;
};

const extractTemplateId = (text: string): string | undefined => {
  const match = TEMPLATE_ID_RE.exec(text);
  return match?.[1]?.trim() || undefined;
};

const splitLabelMetadata = (
  text: string
): {
  labelText: string;
  sessionId?: string;
  documentId?: string;
  patientId?: string;
  memoryId?: string;
  workflowId?: string;
  templateId?: string;
} => {
  const parts = text
    .split(/\s*[|·]\s*/)
    .map((part) => part.trim())
    .filter(Boolean);
  if (parts.length <= 1) {
    const sessionId = extractSessionId(text);
    return {
      labelText: stripSessionParenSuffix(text.trim()),
      sessionId,
      documentId: extractDocumentId(text),
      patientId: extractPatientId(text),
      memoryId: extractMemoryId(text),
      workflowId: extractWorkflowId(text),
      templateId: extractTemplateId(text),
    };
  }

  const metadataParts: string[] = [];
  while (parts.length > 1 && METADATA_SEGMENT_RE.test(parts[parts.length - 1] ?? '')) {
    const segment = parts.pop();
    if (segment) metadataParts.unshift(segment);
  }

  const labelText = parts.join(' | ').trim();
  const metadataText = metadataParts.join(' | ');
  return {
    labelText,
    sessionId: extractSessionId(metadataText),
    documentId: extractDocumentId(metadataText),
    patientId: extractPatientId(metadataText),
    memoryId: extractMemoryId(metadataText),
    workflowId: extractWorkflowId(metadataText),
    templateId: extractTemplateId(metadataText),
  };
};

/**
 * Turn model-authored labels like "Consult note, 21 Aug 2026" or
 * "Session: Waverly Reese (21 Aug)" into structured metadata for chips.
 */
export const parseSourceGroundedLabel = (label: string): ParsedSourceGroundedLabel => {
  const fallbackSessionId = extractSessionId(label);
  const {
    labelText,
    sessionId,
    documentId,
    patientId,
    memoryId,
    workflowId,
    templateId,
  } = splitLabelMetadata(sanitizeModelLabel(label));
  const trimmed = labelText;
  if (!trimmed) {
    return { kind: 'unknown', title: label, displayLabel: label };
  }

  const withIds = {
    sessionId: sessionId ?? fallbackSessionId,
    documentId,
    patientId,
    memoryId,
    workflowId,
    templateId,
  };

  const sessionMatch = trimmed.match(/^Session:\s*(.+?)\s*\(([^)]+)\)\s*$/i);
  if (sessionMatch?.[1] && sessionMatch[2]) {
    const title = sessionMatch[1].trim();
    const subtitle = sessionMatch[2].trim();
    return {
      kind: 'session',
      title,
      subtitle,
      ...withIds,
      displayLabel: subtitle ? `${title} · ${subtitle}` : title,
    };
  }

  const commaParts = trimmed.split(/,\s+/);
  if (commaParts.length >= 2) {
    const lastPartRaw = commaParts[commaParts.length - 1] ?? '';
    const lastPart = stripSessionParenSuffix(lastPartRaw);
    if (isDateLike(lastPart)) {
      const title = commaParts.slice(0, -1).join(', ').trim();
      return {
        kind: detectKind(title),
        title,
        subtitle: lastPart.trim(),
        ...withIds,
        displayLabel: `${title} · ${lastPart.trim()}`,
      };
    }

    const title = commaParts[0]?.trim() ?? trimmed;
    const subtitle = commaParts.slice(1).join(', ').trim();
    if (title && subtitle) {
      return {
        kind: detectKind(title),
        title,
        subtitle,
        ...withIds,
        displayLabel: `${title} · ${subtitle}`,
      };
    }
  }

  const parenDateMatch = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (parenDateMatch?.[1] && parenDateMatch[2] && isDateLike(parenDateMatch[2])) {
    const title = parenDateMatch[1].trim();
    const subtitle = parenDateMatch[2].trim();
    return {
      kind: detectKind(title),
      title,
      subtitle,
      ...withIds,
      displayLabel: `${title} · ${subtitle}`,
    };
  }

  return {
    kind: detectKind(trimmed),
    title: trimmed,
    ...withIds,
    displayLabel: trimmed,
  };
};

/** Reject tool noise, markdown artifacts, and other non-clinical labels. */
export const isValidSourceLabel = (label: string): boolean => {
  const trimmed = label.trim();
  if (trimmed.length < SOURCE_LABEL_MIN_LEN || trimmed.length > SOURCE_LABEL_MAX_LEN) {
    return false;
  }
  if (MARKDOWN_OR_HTML_RE.test(trimmed) || URL_RE.test(trimmed)) return false;
  if (/^[\d\s.,:;|/-]+$/.test(trimmed)) return false;
  return true;
};

const SESSION_QUOTED_NAME_RE = /^Session\s+['"](.+?)['"]\s*$/i;

/** Prose shorthand like "Session A" — not a real session title for chip headings. */
const SESSION_LETTER_ALIAS_RE = /^Session\s+[A-Z]\b$/i;

const cleanSessionSubtitle = (subtitle: string): string => {
  const quoted = subtitle.match(SESSION_QUOTED_NAME_RE)?.[1];
  if (quoted) return quoted.trim();
  const trimmed = subtitle.trim();
  if (SESSION_LETTER_ALIAS_RE.test(trimmed)) return trimmed;
  const withoutPrefix = trimmed.replace(/^Session\s+/i, '').trim();
  return withoutPrefix.length > 2 ? withoutPrefix : trimmed;
};

const extractDateFromLabel = (label: string): string | undefined => {
  for (const part of label.split(/,\s+/).slice(1)) {
    const cleaned = part.split('|')[0]?.trim() ?? '';
    if (isDateLike(cleaned)) return cleaned;
  }
  return undefined;
};

/** Popover/footer heading — prefer session name over redundant kind labels. */
export const displayHeadingForSource = (
  intl: IntlShape,
  reference: {
    label: string;
    title?: string;
    subtitle?: string;
    kind?: SourceGroundedSourceKind;
  }
): { heading: string; meta?: string } => {
  const parsed = parseSourceGroundedLabel(reference.label);
  const kind = reference.kind ?? parsed.kind;
  // Comparisons against the model's free-text label stay on the English
  // canonical name; only what the user sees is localized.
  const canonicalKindName = kindCanonicalName(kind);
  const kindName = kindDisplayName(intl, kind);
  const title = reference.title ?? parsed.title;
  const subtitle = reference.subtitle ?? parsed.subtitle;

  const sessionName =
    subtitle?.match(SESSION_QUOTED_NAME_RE)?.[1] ??
    title.match(/Session\s+['"](.+?)['"]/i)?.[1];

  if (sessionName) {
    return {
      heading: sessionName,
      meta:
        extractDateFromLabel(reference.label) ??
        (subtitle && isDateLike(subtitle) ? subtitle : undefined),
    };
  }

  if (title.toLowerCase() === canonicalKindName.toLowerCase() && subtitle) {
    if (isDateLike(subtitle)) {
      return {
        heading: kindName,
        meta: subtitle.trim(),
      };
    }
    return {
      heading: cleanSessionSubtitle(subtitle),
      meta: extractDateFromLabel(reference.label),
    };
  }

  const kindPrefix = new RegExp(
    `^${canonicalKindName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*,\\s*`,
    'i'
  );
  let heading = stripLinkageLabelNoise(title.replace(kindPrefix, '').trim() || title);
  const meta =
    subtitle && isDateLike(subtitle) ? subtitle : extractDateFromLabel(reference.label);

  if (!heading || isDateLike(heading)) {
    heading = kindName;
  }

  return {
    heading,
    meta,
  };
};

/**
 * English canonical kind names, used ONLY to parse/dedupe against the model's
 * free-text source labels (which are English). Never rendered — the
 * user-visible name is {@link kindDisplayName}.
 */
const KIND_CANONICAL_NAMES: Record<
  Exclude<SourceGroundedSourceKind, 'unknown'>,
  string
> = {
  consult_note: 'Consult note',
  transcript: 'Transcript',
  session_context: 'Session context',
  prechart: 'Pre-chart',
  patient_summary: 'Patient summary',
  patient_profile: 'Patient profile',
  library_document: 'Library document',
  attachment: 'Attachment',
  medications: 'Medications',
  allergies: 'Allergies',
  problems: 'Problems',
  lab_result: 'Lab result',
  vital_signs: 'Vital signs',
  imaging: 'Imaging',
  open_task: 'Open task',
  ehr_record: 'EHR record',
  calendar_event: 'Calendar event',
  agent_memory: 'Agent memory',
  routine: 'Routine',
  template: 'Template',
  email: 'Email',
  fax: 'Fax',
  document: 'Document',
  session: 'Session',
};

const kindCanonicalName = (kind: SourceGroundedSourceKind): string =>
  kind === 'unknown' ? 'Source' : KIND_CANONICAL_NAMES[kind];

// Citation-chip kind names. Keys mirror SourceGroundedSourceKind ('unknown'
// falls back to `source`).
const KIND_DISPLAY_MESSAGES = defineMessages({
  consult_note: {
    defaultMessage: 'Consult note',
    id: 'kbJKH9jHYy',
    description: 'Citation source kind name',
  },
  transcript: {
    defaultMessage: 'Transcript',
    id: 'qim0Vm+O3d',
    description: 'Citation source kind name',
  },
  session_context: {
    defaultMessage: 'Session context',
    id: 'uzOrTgGfxx',
    description: 'Citation source kind name',
  },
  prechart: {
    defaultMessage: 'Pre-chart',
    id: '7AajAO/SwI',
    description: 'Citation source kind name',
  },
  patient_summary: {
    defaultMessage: 'Patient summary',
    id: 'gJGi6Avh6S',
    description: 'Citation source kind name',
  },
  patient_profile: {
    defaultMessage: 'Patient profile',
    id: '0Po6It2XOP',
    description: 'Citation source kind name',
  },
  library_document: {
    defaultMessage: 'Library document',
    id: 'm63x02vZw/',
    description: 'Citation source kind name',
  },
  attachment: {
    defaultMessage: 'Attachment',
    id: 'wW+/aB1leM',
    description: 'Citation source kind name',
  },
  medications: {
    defaultMessage: 'Medications',
    id: 'HMyFFhBLoP',
    description: 'Citation source kind name',
  },
  allergies: {
    defaultMessage: 'Allergies',
    id: 'lipeoIjWjc',
    description: 'Citation source kind name',
  },
  problems: {
    defaultMessage: 'Problems',
    id: 'hnzwX90hNT',
    description: 'Citation source kind name (diagnoses / active issues)',
  },
  lab_result: {
    defaultMessage: 'Lab result',
    id: '/sqnqzR0kK',
    description: 'Citation source kind name',
  },
  vital_signs: {
    defaultMessage: 'Vital signs',
    id: 'R/VN5drTXI',
    description: 'Citation source kind name',
  },
  imaging: {
    defaultMessage: 'Imaging',
    id: 'gEMUvtTg2/',
    description: 'Citation source kind name',
  },
  open_task: {
    defaultMessage: 'Open task',
    id: 'yi4q1CgIkV',
    description: 'Citation source kind name',
  },
  ehr_record: {
    defaultMessage: 'EHR record',
    id: 'ixp9NvRf/f',
    description: 'Citation source kind name',
  },
  calendar_event: {
    defaultMessage: 'Calendar event',
    id: 'ChRENSrp7z',
    description: 'Citation source kind name',
  },
  agent_memory: {
    defaultMessage: 'Agent memory',
    id: 'b5FfEJnnhw',
    description: 'Citation source kind name',
  },
  routine: {
    defaultMessage: 'Routine',
    id: 'jdcRID5m10',
    description: 'Citation source kind name',
  },
  template: {
    defaultMessage: 'Template',
    id: 'OV/d648hGB',
    description: 'Citation source kind name',
  },
  email: {
    defaultMessage: 'Email',
    id: 'O5as9VY5K4',
    description: 'Citation source kind name',
  },
  fax: {
    defaultMessage: 'Fax',
    id: 'I2RpB6QnEO',
    description: 'Citation source kind name',
  },
  document: {
    defaultMessage: 'Document',
    id: 'rRBB0lP40m',
    description: 'Citation source kind name',
  },
  session: {
    defaultMessage: 'Session',
    id: 'ZGQ6eZ/55R',
    description: 'Citation source kind name',
  },
  source: {
    defaultMessage: 'Source',
    id: 'FgdzgzTRiN',
    description: 'Citation source kind name (unknown kind fallback)',
  },
});

export const kindDisplayName = (
  intl: IntlShape,
  kind: SourceGroundedSourceKind
): string =>
  intl.formatMessage(
    kind === 'unknown' ? KIND_DISPLAY_MESSAGES.source : KIND_DISPLAY_MESSAGES[kind]
  );
